import { Component, OnInit, OnDestroy, ElementRef, ViewChild, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignService } from '../../core/services/api/campaign.service';
import { InvestmentService } from '../../core/services/api/investment.service';
import { AccountService } from '../../core/services/api/account.service';
import { AuthService } from '../../core/services/auth/auth.service';
import { ErrorService } from '../../core/services/error.service';
import { StorageService } from '../../core/services/storage.service';
import { Role } from '../../core/enum/role.enum';
import { CampaignStatus } from '../../core/enum/campaign-status.enum';
import { ShareType } from '../../core/enum/share-type.enum';
import { Impact, PartnershipBacked } from '../../core/enum/campaign-categorisation.enum';
import { PopupService } from '../../shared/services/popup/popup.service';
import { finalize } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GtmDataLayerService } from '../../core/services/gtm-data-layer.service';
import { isPlatformBrowser } from '@angular/common';
import posthog from 'posthog-js';
import { environment } from '../../../environments/environment';

export enum FieldType {
  description = 1,
  text = 2,
  carousel = 3,
  highlights = 4,
  image = 5,
  video = 6
}

interface StatusDisplayConfig {
  badgeText: string;
  badgeClass: string;
  metricLabel: string;
  primaryText: string;
  isFinalState: boolean;
  showStats: boolean;
  showDaysLeft: boolean;
  /** Headline label for the collapsed mobile bar, where the raise is already over. */
  collapsedLabel?: string;
}

@Component({
    selector: 'app-campaign-public-summary',
    templateUrl: './campaign-public-summary.component.html',
    styleUrls: ['./campaign-public-summary.component.scss'],
    standalone: false
})
export class CampaignPublicSummaryComponent implements OnInit, OnDestroy {
  FieldType = FieldType;
  CampaignStatus = CampaignStatus;
  Impact = Impact;
  PartnershipBacked = PartnershipBacked;
  // The Shariah sector duplicates the ShariahCompliant impact tag, so it is dropped
  // from the sector list — same exclusion the campaign cards pass to <app-tag-list>.
  private static readonly EXCLUDED_SECTORS = ['Shariah'];
  slug: string;
  summary: any;
  isLoading = false;

  // Cache for sanitized URLs to prevent re-renders
  private safeUrlCache = new Map<string, SafeResourceUrl>();
  private imageUrlCache = new Map<string, string>();
  notFound = false;
  isInvestmentTermsCollapsed = true;
  isMobileExpanded = false;
  isLogin = false;
  isIssuer = false;
  isInvestNowLoading = false;
  private pageStartTime: number;
  private isBrowser: boolean;

  // A setter, not a plain @ViewChild read once in ngAfterViewInit: the bar sits inside
  // the *ngIf that waits on the summary request, so at ngAfterViewInit it does not exist
  // yet and the observer was never attached at all - --mobile-bottom-bar-height stayed
  // unpublished for the life of the page. This fires again each time the bar enters or
  // leaves the view.
  @ViewChild('stickyBarRef')
  set stickyBarRef(ref: ElementRef | undefined) {
    this._stickyBarRef = ref;
    if (this.isBrowser) {
      this.observeStickyBarHeight();
    }
  }
  private _stickyBarRef?: ElementRef;

  private stickyBarResizeObserver?: ResizeObserver;

  // Must match Constants.CAMPAIGN_CONTAINER ("campaign") in the pitchinAPI repo
  // (Commons/PitchIn.Commons/Constants.cs). It is the only container BlobProxy
  // accepts — it rejects anything else with 400 "invalid container".
  private static readonly PROXIED_CONTAINER = 'campaign';

  private static readonly RING_RADIUS = 46;
  private static readonly RING_CIRCUMFERENCE = 2 * Math.PI * CampaignPublicSummaryComponent.RING_RADIUS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private campaignService: CampaignService,
    private investmentService: InvestmentService,
    private accountService: AccountService,
    private authService: AuthService,
    private errorService: ErrorService,
    private storageService: StorageService,
    private popupService: PopupService,
    private sanitizer: DomSanitizer,
    private _gtmDataLayerService: GtmDataLayerService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.pageStartTime = performance.now();
    this.isLogin = this.authService.isAuthenticated();
    this.isIssuer = this.storageService.role === Role.IssuerRep;
    this.route.paramMap.subscribe((params) => {
      this.slug = params.get('slug');
      if (this.slug) {
        this.loadSummary();
      }
    });
  }

  ngOnDestroy() {
    if (this.summary) {
      const duration = Math.round((performance.now() - this.pageStartTime) / 1000);
      this._gtmDataLayerService.logEvent('summary_page_duration', {
        campaign_slug: this.slug,
        duration_seconds: duration,
      });
    }
    this.stickyBarResizeObserver?.disconnect();
    if (this.isBrowser) {
      document.documentElement.style.removeProperty('--mobile-bottom-bar-height');
    }
  }

  // Publishes the height of this page's fixed mobile bottom bar, whose height changes
  // as it collapses/expands. Two things read it: the app-level "back to top" FAB
  // (app.component), which pins itself just above the bar, and this page's own .page
  // bottom padding, which reserves exactly enough room to scroll the last block clear
  // of it.
  private observeStickyBarHeight() {
    this.stickyBarResizeObserver?.disconnect();
    const el = this._stickyBarRef?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') {
      // Nothing on screen to sit above, so the variable must not keep reserving space.
      document.documentElement.style.removeProperty('--mobile-bottom-bar-height');
      return;
    }
    const setHeight = () => {
      document.documentElement.style.setProperty('--mobile-bottom-bar-height', el.offsetHeight + 'px');
    };
    this.stickyBarResizeObserver = new ResizeObserver(setHeight);
    this.stickyBarResizeObserver.observe(el);
    setHeight();
  }

  loadSummary() {
    // Clear cache when loading new data
    this.safeUrlCache.clear();
    this.imageUrlCache.clear();

    this.isLoading = true;
    this.notFound = false;
    this.campaignService.getPublicCampaignSummary(this.slug)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          this.summary = data;
          if (!this.route.snapshot.data?.title) {
            this._gtmDataLayerService.logPageView(
              this.router.url,
              'Campaign Summary - ' + (data.companyName || this.slug),
            );
          }

          this._gtmDataLayerService.logEvent('campaign_view', {
            slug: this.slug,
            campaign_status: data.campaignStatus,
          });
          posthog.capture('campaign_view', {
            slug: this.slug,
            campaign_status: data.campaignStatus,
          });
          if (this.isBrowser) {
            setTimeout(() => {
              this.observeStickyBarHeight();
            }, 0);
          }
        },
        error: () => {
          this.notFound = true;
        },
      });
  }

  get firstField(): any {
    return this.summary?.fields?.length ? this.summary.fields[0] : null;
  }

  get fields(): any[] {
    return this.summary?.fields?.slice(1) || [];
  }

  get visibleSectors(): any[] {
    return (this.summary?.sectors || []).filter(
      (s: any) => !CampaignPublicSummaryComponent.EXCLUDED_SECTORS.includes(s?.name),
    );
  }

  get shareTypeLabel(): string {
    const st = this.summary?.shareType;
    if (!st) return 'OS';
    const num = parseInt(st, 10);
    if (isNaN(num)) return st;
    const key = ShareType[num];
    return key || 'OS';
  }

  get hasSummary(): boolean {
    return !!(this.summary?.isSummaryPublished) && this.fields.length > 0;
  }

  // InProcess campaigns aren't public yet, but issuers/admins preview the one-pager
  // before approving it for launch — render it the same way as a PreLive campaign.
  get isPreLiveTemplate(): boolean {
    const status = this.summary?.campaignStatus;
    return status === CampaignStatus.PreLive || status === CampaignStatus.InProcess;
  }

  get statusConfig(): StatusDisplayConfig {
    const s = this.summary;
    if (!s) return null;
    const status: CampaignStatus = s.campaignStatus;

    switch (status) {
      case CampaignStatus.InProcess:
        return {
          badgeText: 'campaignSummary.status.inProcess',
          badgeClass: 'orange',
          metricLabel: 'campaignSummary.metricLabel.minTarget',
          primaryText: 'campaign.reserveYourInvestment',
          isFinalState: false,
          showStats: false,
          showDaysLeft: false,
        };
      case CampaignStatus.PreLive:
        return {
          badgeText: 'campaignSummary.status.preLive',
          badgeClass: 'orange',
          metricLabel: 'campaignSummary.metricLabel.minTarget',
          primaryText: 'campaign.reserveYourInvestment',
          isFinalState: false,
          showStats: false,
          showDaysLeft: false,
        };
      case CampaignStatus.Live:
        return {
          badgeText: 'campaignSummary.status.live',
          badgeClass: 'green',
          metricLabel: 'campaignSummary.metricLabel.amountRaised',
          primaryText: 'campaignSummary.investNow',
          isFinalState: false,
          showStats: true,
          showDaysLeft: true,
        };
      case CampaignStatus.Cooling:
        return {
          badgeText: 'campaignSummary.status.cooling',
          badgeClass: 'blue',
          metricLabel: 'campaignSummary.metricLabel.amountRaised',
          primaryText: 'campaignSummary.investNow',
          isFinalState: false,
          showStats: true,
          showDaysLeft: true,
        };
      case CampaignStatus.ShareAllotment:
        return {
          badgeText: 'campaignSummary.status.shareAllotment',
          badgeClass: 'allotment',
          metricLabel: 'campaignSummary.metricLabel.successfullyRaised',
          collapsedLabel: 'campaignSummary.collapsedLabel.targetReached',
          primaryText: 'campaignSummary.viewCampaign',
          isFinalState: true,
          showStats: true,
          showDaysLeft: false,
        };
      case CampaignStatus.Success:
        return {
          badgeText: 'campaignSummary.status.success',
          badgeClass: 'green',
          metricLabel: 'campaignSummary.metricLabel.successfullyRaised',
          collapsedLabel: 'campaignSummary.collapsedLabel.targetReached',
          primaryText: 'campaignSummary.viewCampaign',
          isFinalState: true,
          showStats: true,
          showDaysLeft: false,
        };
      default:
        return {
          badgeText: 'campaignSummary.status.ended',
          badgeClass: 'orange',
          metricLabel: 'campaignSummary.metricLabel.amountRaised',
          primaryText: 'campaignSummary.viewCampaign',
          isFinalState: true,
          showStats: true,
          showDaysLeft: false,
        };
    }
  }

  get metricValue(): string {
    const s = this.summary;
    if (!s) return '';
    if (this.isPreLiveTemplate) {
      return this.formatCurrency(s.minimumTargetAmount ?? 0);
    }
    return this.formatCurrency(s.bankedInAmount ?? 0);
  }

  /**
   * Step the headline figure down when it runs long, so it keeps fitting beside the
   * logo on a phone. At 26px the column has room for roughly 12 characters before it
   * outgrows the ~199px left over next to an 80px logo.
   */
  get metricValueSizeClass(): string {
    // Thresholds are where the figure stops fitting beside the logo on a phone at the
    // sizes below — 12 characters is the last that fits at 26px on a 375px screen.
    // Desktop has room to spare and keeps 40px at every length.
    const len = this.metricValue.length;
    if (len > 18) return 'is-xxlong';
    if (len > 14) return 'is-xlong';
    if (len > 12) return 'is-long';
    return '';
  }

  get isCompletedStage(): boolean {
    const status = this.summary?.campaignStatus;
    return status === CampaignStatus.ShareAllotment || status === CampaignStatus.Success;
  }

  /**
   * Status accent for the things that describe the *campaign state* rather than its
   * progress: the days-left figure and the mobile days-left pill.
   *
   * Cooling off is blue — the same blue as its status badge.
   */
  get statusAccentClass(): string {
    const status = this.summary?.campaignStatus;
    if (status === CampaignStatus.Cooling) return 'blue';
    if (status === CampaignStatus.Live) return 'red';
    return 'green';
  }

  /**
   * Progress indicators — the desktop ring and the mobile bar — answer "is the target
   * met?", not "what state is the campaign in". Green once the raise reaches 100%, red
   * below it, at every status.
   *
   * Deliberately separate from statusAccentClass: a cooling-off campaign at 82% shows a
   * red ring beside a blue days-left figure, and that is intended.
   */
  get progressColorClass(): string {
    return this.displayPercent >= 100 ? 'green' : 'red';
  }

  /**
   * Clamped to 100 — this drives the ring's arc and the bar's width, neither of which
   * can go past full. Use displayPercentLabel for the number on screen.
   *
   * Floor, not round: 99.99% must not read as 100%. Rounding up made a campaign that
   * had NOT hit its target show "100% funded" and — since progressColorClass keys off
   * this — turn the bar green. Only a genuine 100% may do that.
   */
  get displayPercent(): number {
    const pct = this.summary?.raisedPercentage;
    return pct != null ? Math.min(Math.floor(pct), 100) : 0;
  }

  get ringCircumference(): number {
    return CampaignPublicSummaryComponent.RING_CIRCUMFERENCE;
  }

  get ringOffset(): number {
    return this.ringCircumference - (this.displayPercent / 100) * this.ringCircumference;
  }

  /**
   * The number shown to the reader. NOT clamped: an oversubscribed campaign really is
   * at 105%, and clamping it hid that. Floored for the same reason as displayPercent —
   * 99.99% must not read as 100%.
   */
  get displayPercentLabel(): string {
    const pct = this.summary?.raisedPercentage;
    return (pct != null ? Math.floor(pct) : 0) + '%';
  }

  get targetLabel(): string {
    const target = this.summary?.minimumTargetAmount;
    if (!target) return '';
    if (target >= 1000000) return 'RM' + (target / 1000000).toFixed(1) + 'M';
    if (target >= 1000) return 'RM' + (target / 1000).toFixed(0) + 'K';
    return 'RM' + Number(target).toFixed(0);
  }

  // Template-facing amount formatter. Angular's currency pipe renders MYR's symbol
  // as "MYR" under the app's en-US locale, so the investment-terms rows used to read
  // "MYR0" — this keeps every amount on the page on the same "RM" formatting.
  amount(value: number, fractionDigits = 0): string {
    return this.formatCurrency(value, fractionDigits);
  }

  /**
   * Cents only when the amount actually has any — "RM 159,428.90" keeps them,
   * "RM 300,000" does not trail a bare .00. Every frame across pre-live, live and
   * cooling follows that rule.
   *
   * Pass fractionDigits to override; amount() pins the investment-terms rows to whole
   * ringgit regardless.
   */
  private formatCurrency(value: number, fractionDigits?: number): string {
    if (value == null || isNaN(value)) return '-';
    const digits = fractionDigits ?? (Math.round(value * 100) % 100 === 0 ? 0 : 2);
    const formatter = new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    // en-MY already yields "RM" on full-ICU builds; the replace covers the trimmed
    // ICU data some runtimes ship, which falls back to the "MYR" code.
    return formatter.format(value).replace('MYR', 'RM');
  }

  private cleanUrl(url: string): string {
    if (!url) return '';
    // Trim whitespace and remove surrounding backticks
    return url.trim().replace(/^`+|`+$/g, '');
  }

  private toGoogleSlidesEmbedUrl(url: string): string {
    // Published Google Slides links use /presentation/d/e/{id}/pubembed — the "e" segment
    // is literal, not part of the id. Matches PitchinAdminWeb's BlockNode.matchSlides()
    // (summary-block.component.ts) so both apps embed the same URL the same way.
    try {
      const parsed = new URL(url);
      const idMatch = parsed.pathname.match(/^\/presentation\/d\/e\/([^/]+)\/pubembed$/);
      const slidesId = idMatch?.[1];
      if (parsed.hostname !== 'docs.google.com' || !slidesId) return url;
      const query = parsed.search.slice(1) || 'start=false&loop=false&delayms=3000';
      return `https://docs.google.com/presentation/d/e/${slidesId}/pubembed?${query}`;
    } catch {
      return url;
    }
  }

  getSafeUrl(url: string): SafeResourceUrl {
    const cleanedUrl = this.cleanUrl(url);
    if (!cleanedUrl) {
      return this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
    }

    // Check cache first
    if (this.safeUrlCache.has(cleanedUrl)) {
      return this.safeUrlCache.get(cleanedUrl)!;
    }

    try {
      const parsed = new URL(cleanedUrl);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        const result = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
        this.safeUrlCache.set(cleanedUrl, result);
        return result;
      }
      const embedUrl = this.toGoogleSlidesEmbedUrl(parsed.toString());
      const result = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
      this.safeUrlCache.set(cleanedUrl, result);
      return result;
    } catch {
      const result = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
      this.safeUrlCache.set(cleanedUrl, result);
      return result;
    }
  }

  private toBlobProxyUrl(url: string): string {
    try {
      const parsed = new URL(url);
      const host = parsed.hostname;
      // Match Azure blob storage URLs (e.g. storage.pitchin.my or *.blob.core.windows.net)
      if (host.includes('storage.pitchin.my') || host.endsWith('.blob.core.windows.net')) {
        // `.pathname` keeps its percent-encoding (e.g. a space stays "%20") — decode
        // before re-encoding as a query param, or "%20" becomes "%2520" and the blob
        // lookup 404s.
        const pathParts = parsed.pathname.split('/').filter(p => p).map(p => decodeURIComponent(p));
        if (pathParts.length >= 2) {
          const container = pathParts[0];
          // BlobProxyController serves the campaign container only, and rejects anything
          // else with 400 "invalid container". Other containers don't need it anyway:
          // companylogo/companybanner are "Images"-group attachments whose stored Url is a
          // durable stored-access-policy SAS (AttachmentService.UploadFileAsAttachmentWithSAS),
          // so proxying them only broke the logo. Leave them pointing at blob storage.
          if (container.toLowerCase() !== CampaignPublicSummaryComponent.PROXIED_CONTAINER) return url;
          const blob = pathParts.slice(1).join('/');
          return `${environment.apiServer}api/v1/BlobProxy/image?container=${encodeURIComponent(container)}&blob=${encodeURIComponent(blob)}`;
        }
      }
    } catch {
      // not a URL, return as-is
    }
    return url;
  }

  getImageUrl(url: string): string {
    const cleanedUrl = this.cleanUrl(url);
    if (!cleanedUrl) return '';

    // Check cache first
    if (this.imageUrlCache.has(cleanedUrl)) {
      return this.imageUrlCache.get(cleanedUrl)!;
    }

    const withProtocol = cleanedUrl.startsWith('http') ? cleanedUrl : 'https://' + cleanedUrl;
    const result = this.toBlobProxyUrl(withProtocol);
    this.imageUrlCache.set(cleanedUrl, result);
    return result;
  }

  shareCampaign() {
    if (!this.isBrowser) return;

    const shareUrl = `${window.location.origin}/campaign-summary/${encodeURIComponent(this.slug)}`;
    const shareData = {
      title: this.summary?.companyName || '',
      text: this.summary?.companyTagline || '',
      url: shareUrl,
    };

    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
      return;
    }

    // Fallback: copy link (or at minimum show a prompt) without injecting HTML into a new window.
    const writeText = navigator.clipboard?.writeText?.bind(navigator.clipboard);
    if (writeText) {
      writeText(shareUrl).catch(() => {
        window.prompt('Copy this link', shareUrl);
      });
    } else {
      window.prompt('Copy this link', shareUrl);
    }
  }

  findOutMore() {
    this._gtmDataLayerService.logEvent('click_summary_find_out_more', {
      campaign_slug: this.slug,
    });
    this.router.navigate(['/equity', this.slug]);
  }

  investNow() {
    if (this.isInvestNowLoading) return;

    const config = this.statusConfig;
    if (config?.isFinalState) {
      this._gtmDataLayerService.logEvent('click_summary_view_campaign', {
        campaign_slug: this.slug,
      });
      this.router.navigate(['/equity', this.slug]);
      return;
    }

    if (!this.isLogin) {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: this.router.url },
      });
      return;
    }

    if (this.isIssuer) {
      this.popupService.alert('common.pleaseLoginAsInvestor');
      return;
    }

    this.isInvestNowLoading = true;
    this.investmentService.getInvestmentEligibility().subscribe({
      next: (data) => {
        this.isInvestNowLoading = false;
        if (data) {
          this._gtmDataLayerService.logEvent('click_summary_invest_now', {
            campaign_slug: this.slug,
            campaign_name: this.summary?.companyName,
          });
          this.router.navigate(['/equity', this.slug, 'invest-now']);
        } else {
          this.popupService
            .alert('campaign.notEligibleToInvest', {
              titleLabel: 'campaign.completeProfileForInvest',
              okLabel: 'campaign.completeMyProfile',
              cancelLabel: '',
              backdrop: true,
            })
            .then(() => {
              this.accountService.profile(true, true).subscribe({
                error: (err) => this.errorService.showError(err),
              });
            })
            .catch(() => {});
        }
      },
      error: (err) => {
        this.isInvestNowLoading = false;
        this.errorService.showError(err);
      },
    });
  }

  openHelp() {
    const chatbotUrl = this.summary?.chatbotUrl;
    if (chatbotUrl) {
      this._gtmDataLayerService.logEvent('click_summary_help', {
        campaign_slug: this.slug,
      });
      window.open(chatbotUrl, '_blank', 'noopener,noreferrer');
    }
  }

  toggleInvestmentTerms() {
    this.isInvestmentTermsCollapsed = !this.isInvestmentTermsCollapsed;
  }

  toggleMobileExpanded() {
    this.isMobileExpanded = !this.isMobileExpanded;
  }

  get investNowLabel(): string {
    return this.summary?.isInvestable
      ? 'campaignSummary.investNow'
      : 'campaignSummary.reserveYourInvestment';
  }

  onCarouselClick(field: any) {
    this._gtmDataLayerService.logEvent('click_summary_carousel', {
      campaign_slug: this.slug,
      carousel_header: field?.headerName,
    });
  }

  // Convert a YouTube/Vimeo URL to its embeddable form; null if not a recognized video URL.
  private toEmbedUrl(raw: string): string | null {
    const url = this.cleanUrl(raw);
    if (!url) return null;
    const yt = url.match(/^.*(youtu\.be\/|shorts\/|live\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
    if (yt && yt[2].length === 11) return 'https://www.youtube.com/embed/' + yt[2] + '?rel=0&modestbranding=1';
    const vm = url.match(/(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/);
    if (vm && vm[4]) return 'https://player.vimeo.com/video/' + vm[4];
    return null;
  }

  // Sanitized embed URL for the <iframe>, or null → show the fallback link.
  getVideoEmbedUrl(raw: string): SafeResourceUrl | null {
    const embed = this.toEmbedUrl(raw);
    return embed ? this.getSafeUrl(embed) : null;
  }

  // Cleaned, https-prefixed original URL for the "Watch on YouTube" link.
  getWatchUrl(raw: string): string {
    return this.getImageUrl(raw);
  }

  getIconName(iconName: string): string {
    return iconName?.startsWith('fa-') ? iconName : 'fa-' + iconName;
  }

  trackByField(index: number, field: any): number {
    return field.sequenceNo ?? index;
  }
}
